import { useState, useEffect, useRef } from "react";
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { Box, Divider, Flex } from "@chakra-ui/react";
import InputTextAreaExpandable from './InputTextAreaExpandable';
import LaunchdeckCloseButton from "../../../global/components/helper-components/components/LaunchdeckCloseButton";
import LaunchdeckButton from "../../../global/components/helper-components/components/LaunchdeckButton";
import useLaunchdeckModal from "../../../global/hooks/use-launchdeck-modal";
import { manageDsrFaqContent, resetManageDsrFaqContent } from "../../../digital-sales-room/actions/dsr-faq-actions";


function DsrFaqTextAreaInput({ 
  htmlFor, name, currentValue, placeholder, userActionRequired,
  dsrId, questionId,
  handleCloseInputArea,
  bg, setActOnQuestionId,
  isRoomTemplate, dsrTemplateId,
  // Redux
  manageFaqProcessingCompleted,

  // Actions
  manageDsrFaqContent,
  resetManageDsrFaqContent
}) {

  const originalValue = useRef(currentValue || null)
  const { openLaunchdeckModal } = useLaunchdeckModal();

  // Inputs
  const [inputValue, setInputValue] = useState(currentValue || "");

  const [showUpdateErrorTag, setShowUpdateErrorTag] = useState(null)
  const [displayEmptyInputError, setDisplayEmptyInputError] = useState(false)
  const [isInvalidInput, setIsInvalidInput] = useState("");

  const [questionIsSaving, setQuestionIsSaving] = useState(false)


  useEffect(() => {
    if (inputValue) {
      setDisplayEmptyInputError(false)
    }
  }, [inputValue]);


    useEffect(() => {
      if (manageFaqProcessingCompleted === true) {
        setShowUpdateErrorTag(false)
        
        setQuestionIsSaving(false)
        setActOnQuestionId("")
        resetManageDsrFaqContent()
        handleCloseInputArea()
      } else if (manageFaqProcessingCompleted === false) {
        setShowUpdateErrorTag(true)
        setQuestionIsSaving(false)
        resetManageDsrFaqContent()
      }
    }, [manageFaqProcessingCompleted, handleCloseInputArea])


  const onChangeHandler = event => {
    if (!inputValue) {
      setDisplayEmptyInputError(false)
    }

    setInputValue(event.target.value);
    setIsInvalidInput("")
  };

  const handleEmptyInput = () => {
    setIsInvalidInput(true)
    setDisplayEmptyInputError(true)
  }

  const handleClickCreateFaqAnswer = () => {
    if (!inputValue) {
      setDisplayEmptyInputError(true)
    } else {

        const editFaqQuestionMetadata = {
          processEvent: 'create-faq-answer',
          payload: {
            dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
            questionId: questionId,
            faqAnswerContent: inputValue,
            isRoomTemplate
          }
        }
        
        manageDsrFaqContent(editFaqQuestionMetadata)
        setQuestionIsSaving(true)
        setInputValue("")
    }
  }

  const handleClickEditFaqAnswer = () => {
    if (!inputValue) {
      setDisplayEmptyInputError(true)
    } else if (originalValue === inputValue) {
      handleCloseInputArea()
      setDisplayEmptyInputError(false)
    } else {
      const editFaqQuestionMetadata = {
        processEvent: 'edit-faq-answer',
        payload: {
          dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
          questionId: questionId,
          faqAnswerContent: inputValue,
          isRoomTemplate
        }
      }
      
      manageDsrFaqContent(editFaqQuestionMetadata)
      setInputValue(currentValue || "")
      setQuestionIsSaving(true)
    }
  }

  const handleClickEditFaqQuestion = () => {
    if (!inputValue) {
      setDisplayEmptyInputError(true)
    } else if (originalValue === inputValue) {
      handleCloseInputArea()
      setDisplayEmptyInputError(false)
    } else {
      const editFaqQuestionMetadata = {
        processEvent: 'edit-faq-question',
        payload: {
          dsrId: isRoomTemplate ? dsrTemplateId : dsrId,
          questionId: questionId,
          faqQuestionContent: inputValue,
          isRoomTemplate
        }
      }
      
      manageDsrFaqContent(editFaqQuestionMetadata)
      setQuestionIsSaving(true)
    }
  }

  const handleClickCloseButton = () => {
    handleCloseInputArea()
    setDisplayEmptyInputError(false)
    setInputValue("")
  }

  const handleConfirmDeleteFaqQuestion = () => {
    openLaunchdeckModal('confirm-delete-faq-question', {
      dsrId,
      questionId
    })
  }


  switch (userActionRequired) {
    case ('create-faq-answer'): {
      return (
        <Box htmlFor={htmlFor} w='100%'>
            <>
              <Flex w='100%' my='0.5em' justify='space-between' align='center'>
                <Box fontSize='0.6em' p='0.25em 0.5em' bg='brandDark.500' color='white' rounded='0.5em'>REPLY TO QUESTION</Box>
                <LaunchdeckCloseButton handleCloseModal={handleClickCloseButton} />
              </Flex>
              <InputTextAreaExpandable
                onChangeHandler={onChangeHandler}
                inputValue={inputValue}
                placeholder={placeholder}
                isInvalidInput={isInvalidInput}
                border='1px solid'
                borderColor='gray.200'
                color='brandDark.500'
                bg={bg && bg}
                isDisabled={questionIsSaving || false}
              />
              {displayEmptyInputError && <Box fontSize="0.8em" color="brandPink.500" mt='0.2em'>Please provide an answer before submitting</Box>}

              <Flex my='1em' justify='flex-end' w='100%'>
                <DisplayButtonMode
                  name={name}
                  showUpdateErrorTag={showUpdateErrorTag}
                  handleCtaButtonClick={handleClickCreateFaqAnswer}
                  handleEmptyInput={handleEmptyInput}
                  submittedInputIsSaving={questionIsSaving}
                  inputValue={inputValue}
                  buttonMode={'create'}
                  buttonText={'Save answer'}
                />

              </Flex>
            </>
        </Box>
      );
    }
    case ('edit-faq-answer'): {
      return (
        <Box htmlFor={htmlFor} w='100%'>
            <>
              <Divider />
              <Flex w='100%' my='1em' justify='space-between' align='center'>
                <Box fontSize='0.6em' p='0.25em 0.5em' bg='brandDark.500' color='white' rounded='0.5em'>EDIT ANSWER</Box>

                <LaunchdeckCloseButton handleCloseModal={handleClickCloseButton} />

              </Flex>

              <InputTextAreaExpandable
                onChangeHandler={onChangeHandler}
                inputValue={inputValue}
                placeholder={placeholder}
                isInvalidInput={isInvalidInput}
                borderColor='gray.500'
                border='1px solid'
                bg={bg && bg}
                isDisabled={questionIsSaving || false}
              />
              {displayEmptyInputError && <Box fontSize="0.8em" color="brandPink.500" mt='0.2em'>Please provide an answer before submitting</Box>}

              <Flex mt='0.5em' justify='flex-end' w='100%'>

                <DisplayButtonMode
                  name={name}
                  showUpdateErrorTag={showUpdateErrorTag}
                  handleCtaButtonClick={handleClickEditFaqAnswer}
                  handleEmptyInput={handleEmptyInput}
                  submittedInputIsSaving={questionIsSaving}
                  inputValue={inputValue}
                  buttonMode={'update'}
                  handleConfirmDeleteFaqQuestion={handleConfirmDeleteFaqQuestion}
                />

              </Flex>
            </>
        </Box>
      );
    }
    case ('edit-faq-question'): {
      return (
        <Box htmlFor={htmlFor} w='100%'>
          <>
            <Flex w='100%' justify='space-between' align='center' px='1em' pt='0.5em'>
              <Box fontSize='0.6em' p='0.25em 0.5em' bg='brandDark.500' color='white' rounded='0.5em'>EDIT QUESTION</Box>
              <LaunchdeckCloseButton hide={questionIsSaving}  handleCloseModal={handleClickCloseButton} />
            </Flex>
            <Box p='1em'>
              <InputTextAreaExpandable
                onChangeHandler={onChangeHandler}
                inputValue={inputValue}
                placeholder={placeholder}
                isInvalidInput={isInvalidInput}
                isLoadingState={questionIsSaving}
                bg={bg && bg}
                isDisabled={questionIsSaving || false}
              />
              {displayEmptyInputError && <Box fontSize="0.8em" color="brandPink.500" mt='0.2em'>Did you want to delete the question? Simply use the delete button below. </Box>}

              <Flex mt='0.5em' justify='flex-end' w='100%'>

                <DisplayButtonMode
                  name={name}
                  showUpdateErrorTag={showUpdateErrorTag}
                  handleCtaButtonClick={handleClickEditFaqQuestion}
                  handleEmptyInput={handleEmptyInput}
                  submittedInputIsSaving={questionIsSaving}
                  inputValue={inputValue}
                  originalValue={originalValue}
                  buttonMode={'update'}
                  buttonText={'Save'}
                  handleConfirmDeleteFaqQuestion={handleConfirmDeleteFaqQuestion}
                />

              </Flex>
            </Box>

          </>
        </Box>
      )
    }
    default: {
      return null;
    }
  }
}

function DisplayButtonMode({
  name,
  showUpdateErrorTag,
  handleCtaButtonClick,
  submittedInputIsSaving,
  handleEmptyInput,
  inputValue,
  buttonMode,
  buttonText,
  handleConfirmDeleteFaqQuestion
}) {

  if (showUpdateErrorTag) {
    return (
      <Box fontSize='0.75em' color='brandDark.500'>Unexpected error EVXXUU. Please try again later.</Box>
    )
  } else {
    return (
      <>
        <Flex mt='0.5em' w='100%' justify={buttonMode === 'create' ? 'flex-end' : 'space-between'} align='flex-end'>
          {(buttonMode === 'update') &&
            <Box mx='0.5em' color='gray.500' cursor='pointer' fontSize='0.75em' onClick={() => handleConfirmDeleteFaqQuestion()}>
              Delete {name === "EditFaqAnswer" ? 'answer' : 'question'}
            </Box>
          }
          <LaunchdeckButton bg='brand.500' color='white' fontSize='0.8em' loadingLogic={submittedInputIsSaving} onClick={() => inputValue.trim().length > 0 ? handleCtaButtonClick() : handleEmptyInput()} label={buttonText ? buttonText : 'Submit'} />

        </Flex>
      </>
    )
  }
}


function mapStateToProps(state) {
  return {
    isRoomTemplate: state.dsrTemplateId.isRoomTemplate,
    dsrTemplateId: state.dsrTemplateId.results,

    manageFaqProcessingCompleted: state.manageDsrFaqContentOutcome?.results?.processingCompleted,
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      manageDsrFaqContent,
      resetManageDsrFaqContent
    },
    dispatch,
  )


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DsrFaqTextAreaInput));